import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Members from "./pages/Members";
import Attendance from "./pages/Attendance";
import Conversions from "./pages/Conversions";
import Messaging from "./pages/Messaging";
import Portal from "./pages/Portal";
import Teams from "./pages/Teams";
import Conversations from "./pages/Conversations";
import PrayerRequests from "./pages/PrayerRequests";
import MyTeams from "./pages/MyTeams";
import Gallery from "./pages/Gallery";
import Sermons from "./pages/Sermons";
import SermonSeries from "./pages/SermonSeries";
import ReplyInbox from "./pages/ReplyInbox";
import Documents from "./pages/Documents";
import CalendarPage from "./pages/Calendar";
import Giving from "./pages/Giving";
import Users from "./pages/Users";
import FollowUp from "./pages/FollowUp";
import Classes from "./pages/Classes";
import CourseDetail from "./pages/CourseDetail";
import FeedbackBoard from "./pages/FeedbackBoard";
import PortalClasses from "./pages/PortalClasses";
import PortalCourseDetail from "./pages/PortalCourseDetail";
import UserGuide from "./pages/UserGuide";
import MessageLog from "./pages/MessageLog";
import MessageTemplates from "./pages/MessageTemplates";
import RolesPermissions from "./pages/RolesPermissions";
import AlertsSettings from "./pages/AlertsSettings";
import CallingAudit from "./pages/CallingAudit";
import AccessOverview from "./pages/AccessOverview";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Connect from "./pages/Connect";
import CellResponseForm from "./pages/CellResponseForm";
import MyCellGroup from "./pages/MyCellGroup";
import Outreach from "./pages/Outreach";
import DeletionLog from "./pages/DeletionLog";
import PortalSettings from "./pages/PortalSettings";
import CellResponses from "./pages/CellResponses";
import CellPlanning from "./pages/CellPlanning";
import CellAudit from "./pages/CellAudit";
import CellOverview from "./pages/CellOverview";
import CellPostcodeGroups from "./pages/CellPostcodeGroups";
import CellDataQuality from "./pages/CellDataQuality";
import ChurchQRCodes from "./pages/ChurchQRCodes";
import SharedQR from "./pages/SharedQR";
import { ConfirmProvider } from "./context/ConfirmContext";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--sj-bg)" }}>
    <div className="h-10 w-10 rounded-full border-4 border-[#EBE9E4] border-t-[#0B5E4E] animate-spin" />
  </div>
);

function StaffRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "member") return <Navigate to="/portal" replace />;
  if (user.role === "team_leader") return <Navigate to="/my-teams" replace />;
  return <Layout>{children}</Layout>;
}

function LeaderRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "member") return <Navigate to="/portal" replace />;
  return <Layout>{children}</Layout>;
}

function MemberRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "member") return <Navigate to={landingFor(user.role)} replace />;
  return <Layout>{children}</Layout>;
}

function AnyAuthRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function GivingRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin" && !user.giving_open) return <Layout><AccessDenied /></Layout>;
  return <Layout>{children}</Layout>;
}

function CapRoute({ cap, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  const caps = Array.isArray(cap) ? cap : [cap];
  const allowed = user.role === "admin" || caps.some((c) => user.caps?.[c]);
  if (!allowed) return <Layout><AccessDenied /></Layout>;
  return <Layout>{children}</Layout>;
}

function AccessDenied() {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="access-denied">
      <div className="max-w-md w-full text-center rounded-2xl border p-10" style={{ borderColor: "var(--sj-border)", background: "#fff" }}>
        <div className="mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-5" style={{ background: "#D9770622" }}>
          <ShieldAlert className="h-7 w-7" style={{ color: "#B45309" }} />
        </div>
        <h1 className="font-head text-2xl font-extrabold mb-2" style={{ color: "var(--sj-text)" }}>Access denied</h1>
        <p className="text-sm mb-6" style={{ color: "var(--sj-text-2)" }}>
          You don't have permission to view this page. Prayer requests and pastoral records are restricted to authorised
          pastoral staff. If you believe you should have access, please contact a church administrator.
        </p>
        <Link to="/dashboard" data-testid="access-denied-home" className="inline-block rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105" style={{ background: "var(--sj-primary)" }}>
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}

function landingFor(role) {
  if (role === "member") return "/portal";
  if (role === "team_leader") return "/my-teams";
  return "/dashboard";
}

function Root() {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={landingFor(user.role)} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ConfirmProvider>
        <BrowserRouter>
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Root />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/connect" element={<Connect />} />
            <Route path="/first-timers" element={<Connect key="visitor" mode="visitor" />} />
            <Route path="/cell-response" element={<CellResponseForm />} />
            <Route path="/outreach" element={<Navigate to="/evangelism" replace />} />
            <Route path="/evangelism" element={<CapRoute cap={["evangelism", "evangelism_follow_up", "manage_members"]}><Outreach key="evangelism" section="evangelism" /></CapRoute>} />
            <Route path="/visitor-care" element={<CapRoute cap={["visitor_care", "manage_members"]}><Outreach key="visitor" section="visitor" /></CapRoute>} />
            <Route path="/dashboard" element={<StaffRoute><Dashboard /></StaffRoute>} />
            <Route path="/members" element={<CapRoute cap="manage_members"><Members /></CapRoute>} />
            <Route path="/attendance" element={<StaffRoute><Attendance /></StaffRoute>} />
            <Route path="/new-members" element={<Navigate to="/follow-up" replace />} />
            <Route path="/pastoral-care" element={<CapRoute cap="pastoral_care"><FollowUp key="pastoral" section="pastoral" /></CapRoute>} />
            <Route path="/follow-up" element={<CapRoute cap="pastoral_care"><FollowUp key="follow_up" section="follow_up" /></CapRoute>} />
            <Route path="/reply-inbox" element={<CapRoute cap="pastoral_care"><ReplyInbox /></CapRoute>} />
            <Route path="/conversions" element={<StaffRoute><Conversions /></StaffRoute>} />
            <Route path="/messaging" element={<StaffRoute><Messaging /></StaffRoute>} />
            <Route path="/message-log" element={<CapRoute cap="staff"><MessageLog /></CapRoute>} />
            <Route path="/message-templates" element={<CapRoute cap="is_pastor"><MessageTemplates /></CapRoute>} />
            <Route path="/conversations" element={<StaffRoute><Conversations /></StaffRoute>} />
            <Route path="/prayer-requests" element={<CapRoute cap="view_prayers"><PrayerRequests /></CapRoute>} />
            <Route path="/teams" element={<StaffRoute><Teams /></StaffRoute>} />
            <Route path="/church-qr-codes" element={<LeaderRoute><ChurchQRCodes /></LeaderRoute>} />
            <Route path="/shared-qr-codes" element={<AnyAuthRoute><SharedQR /></AnyAuthRoute>} />
            <Route path="/users" element={<CapRoute cap="is_admin"><Users /></CapRoute>} />
            <Route path="/deletion-log" element={<CapRoute cap="is_admin"><DeletionLog /></CapRoute>} />
            <Route path="/portal-settings" element={<CapRoute cap="is_admin"><PortalSettings /></CapRoute>} />
            <Route path="/roles-permissions" element={<CapRoute cap="is_admin"><RolesPermissions /></CapRoute>} />
            <Route path="/alerts" element={<CapRoute cap="is_admin"><AlertsSettings /></CapRoute>} />
            <Route path="/calling-audit" element={<CapRoute cap="is_admin"><CallingAudit /></CapRoute>} />
            <Route path="/access-overview" element={<CapRoute cap="is_admin"><AccessOverview /></CapRoute>} />
            <Route path="/documents" element={<CapRoute cap="staff"><Documents /></CapRoute>} />
            <Route path="/calendar" element={<AnyAuthRoute><CalendarPage /></AnyAuthRoute>} />
            <Route path="/giving" element={<GivingRoute><Giving /></GivingRoute>} />
            <Route path="/gallery" element={<AnyAuthRoute><Gallery /></AnyAuthRoute>} />
            <Route path="/sermons" element={<AnyAuthRoute><Sermons /></AnyAuthRoute>} />
            <Route path="/sermons/series/:name" element={<AnyAuthRoute><SermonSeries /></AnyAuthRoute>} />
            <Route path="/classes" element={<CapRoute cap="staff"><Classes /></CapRoute>} />            <Route path="/classes/:id" element={<CapRoute cap="manage_content"><CourseDetail /></CapRoute>} />
            <Route path="/feedback-board" element={<CapRoute cap="manage_content"><FeedbackBoard /></CapRoute>} />
            <Route path="/my-classes" element={<AnyAuthRoute><PortalClasses /></AnyAuthRoute>} />
            <Route path="/my-cell-group" element={<AnyAuthRoute><MyCellGroup /></AnyAuthRoute>} />
            <Route path="/my-classes/:id" element={<AnyAuthRoute><PortalCourseDetail /></AnyAuthRoute>} />
            <Route path="/portal" element={<AnyAuthRoute><Portal /></AnyAuthRoute>} />
            <Route path="/guide" element={<AnyAuthRoute><UserGuide /></AnyAuthRoute>} />
            <Route path="/leader" element={<Navigate to="/my-teams" replace />} />
            <Route path="/my-teams" element={<AnyAuthRoute><MyTeams /></AnyAuthRoute>} />
            <Route path="/cell/overview" element={<CapRoute cap="cell_view"><CellOverview /></CapRoute>} />
            <Route path="/cell/responses" element={<CapRoute cap="cell_view"><CellResponses /></CapRoute>} />
            <Route path="/cell/postcode-groups" element={<CapRoute cap="cell_view"><CellPostcodeGroups /></CapRoute>} />
            <Route path="/cell/planning" element={<CapRoute cap="cell_view"><CellPlanning /></CapRoute>} />
            <Route path="/cell/data-quality" element={<CapRoute cap="cell_view"><CellDataQuality /></CapRoute>} />
            <Route path="/cell/audit" element={<CapRoute cap="cell_view"><CellAudit /></CapRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
        </ConfirmProvider>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
